import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react'

export type SpaceVisitStep = 'intro' | 'day' | 'startTime' | 'payment'

interface SpaceVisitStepperState {
	day: string
	startTime: string | null
	step: SpaceVisitStep
}

interface SpaceVisitStepperContextValue {
	day: string
	setDay: (day: string) => void
	setStartTime: (startTime: string) => void
	setStep: (step: SpaceVisitStep) => void
	startTime: string | null
	step: SpaceVisitStep
}

const SpaceVisitStepperContext =
	createContext<SpaceVisitStepperContextValue | null>(null)

const getInitialState = (): SpaceVisitStepperState => ({
	day: dateTimeUtils().customFormat('dateTech'),
	startTime: null,
	step: 'intro',
})

export const SpaceVisitStepperProvider = ({
	children,
}: PropsWithChildren) => {
	const [state, setState] = useState(getInitialState)

	const setDay = useCallback((day: string) => setState((current) => ({ ...current, day })), [])
	const setStartTime = useCallback(
		(startTime: string) => setState((current) => ({ ...current, startTime })),
		[],
	)
	const setStep = useCallback(
		(step: SpaceVisitStep) => setState((current) => ({ ...current, step })),
		[],
	)

	const value = useMemo(
		() => ({ ...state, setDay, setStartTime, setStep }),
		[state, setDay, setStartTime, setStep],
	)

	return (
		<SpaceVisitStepperContext.Provider value={value}>
			{children}
		</SpaceVisitStepperContext.Provider>
	)
}

export const useSpaceVisitStepper = () => {
	const context = useContext(SpaceVisitStepperContext)

	if (!context) {
		throw new Error(
			'useSpaceVisitStepper must be used within SpaceVisitStepperProvider',
		)
	}

	return context
}
